import { useState, useMemo } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import './App.css';

import PrivateRoute from "./router/PrivateRoute";
import PublicRoute from "./router/PublicRoute";
import ConditionalRoute from "./router/ConditionalRoute";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import { ConfigProvider, Alert } from "antd";
import Signup from "./pages/Signup";
import { AuthProvider } from "./hooks/AuthProvider";
import ThemeProvider from "./utils/ThemeContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { LoadScript } from "@react-google-maps/api";
import SettingsPage from "./pages/Settings";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SettingsPrivacyPolicy from "./pages/PrivacyPolicy";
import Confirm from "./pages/Confirm";
import { useTokenManager } from "./hooks/useTokenManager";
import useGoogleMaps from "./hooks/useGoogleMaps";
import CompanyLayout from "./components/Company/CompanyLayout";
import CompanyOffers from "./pages/Company/CompanyOffers";
import CompanyEmployees from "./pages/Company/CompanyEmployees";
import CompanyDashboardSub from "./pages/Company/CompanyDashboardSub";
import CompanySettingsSub from "./pages/Company/CompanySettingsSub";
import CompanySettings from "./pages/Company/CompanySettings";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

// Admin pages
import AdminLayout from "./components/Admin/AdminLayout";
import AdminOffers from "./pages/Admin/AdminOffers";
import AdminFeed from "./pages/Admin/AdminFeed";
import AdminNotifications from "./pages/Admin/AdminNotifications";
import AdminFilters from "./pages/Admin/AdminFilters";

// Component to handle token management
const TokenManager = ({ children }) => {
  useTokenManager(); // This will handle automatic token refresh
  return children;
};

// Component to handle Google Maps loading
const GoogleMapsProvider = ({ children }) => {
  const { loadError, apiKey, handleLoad, handleError } = useGoogleMaps();
  const [libraries] = useState(['places']);

  // Memoize the API key to prevent unnecessary re-renders
  const memoizedApiKey = useMemo(() => apiKey, [apiKey]);

  if (!memoizedApiKey) {
    return (
      <Alert
        message="Configuration manquante"
        description="La clé API Google Maps n'est pas configurée. Vérifiez votre fichier .env"
        type="error"
        showIcon
        className="m-4"
      />
    );
  }

  if (loadError) {
    return (
      <Alert
        message="Erreur Google Maps"
        description={
          <div>
            <p>Impossible de charger Google Maps : {loadError}</p>
            <p className="mt-2">
              <strong>Solutions :</strong>
            </p>
            <ul className="mt-1 ml-4">
              <li>Vérifiez que votre clé API est valide</li>
              <li>Assurez-vous que localhost:3000 est autorisé dans la console Google Cloud</li>
              <li>Vérifiez que les APIs Maps JavaScript et Places sont activées</li>
            </ul>
          </div>
        }
        type="error"
        showIcon
        className="m-4"
      />
    );
  }

  return (
    <LoadScript
      googleMapsApiKey={memoizedApiKey}
      libraries={libraries}
      onLoad={handleLoad}
      onError={handleError}
    >
      {children}
    </LoadScript>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});


function App() {
  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#6076E7' } }}>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />

        <ToastContainer />
        <div className="App">
          <ThemeProvider>
            <Router>
              <GoogleMapsProvider>
                <AuthProvider>
                  <TokenManager>
                    <Routes>
                      <Route path="/" element={<ConditionalRoute />} />
                      <Route element={<PublicRoute />}>
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/resetPassword" element={<ResetPassword />} />
                        <Route path="/privacyPolicy" element={<SettingsPrivacyPolicy />} />
                        <Route path="/confirm" element={<Confirm />} />
                      </Route>
                      <Route element={<PrivateRoute />}>
                        <Route path="/settings" element={<SettingsPage />} />
                        <Route path="/dashboard" element={<Dashboard />} />

                        {/* Admin routes avec layout imbriqué */}
                        <Route
                          path="/administrator"
                          element={<AdminLayout />}
                        >
                          <Route index element={<Navigate replace to="offers" />} />
                          <Route path="offers" element={<AdminOffers />} />
                          <Route path="feed" element={<AdminFeed />} />
                          <Route path="notification" element={<AdminNotifications />} />
                          <Route path="filters" element={<AdminFilters />} />
                        </Route>

                        {/* Company routes avec layout imbriqué */}
                        <Route
                          path="/company/:id"
                          element={<CompanyLayout />}
                        >
                          <Route index element={<Navigate replace to="offers" />} />
                          <Route path="offers" element={<CompanyOffers />} />
                          <Route path="employees" element={<CompanyEmployees />} />
                          <Route path="settings" element={<CompanySettings />} />
                          <Route path="settings-sub" element={<CompanySettingsSub />} />
                          <Route path="dashboard-sub" element={<CompanyDashboardSub />} />
                        </Route>
                      </Route>
                    </Routes>
                  </TokenManager>
                </AuthProvider>
              </GoogleMapsProvider>
            </Router>
          </ThemeProvider>
        </div>
      </QueryClientProvider>
    </ConfigProvider>
  );
}

export default App;
